import { BenefitFormData } from '@/types/circuits/CircuitDaysFormData';
import { useCallback, useMemo } from 'react';
import { useConvertPrice } from './useCurrencyExchange';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { roundUpPrice } from '@/utils/circuits/prices/formatPrice';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';

type CarCategory =
  Required<BenefitFormData>['carRental']['carCategories'][number];

type CarCategoryTranslation = {
  category?: string;
  model?: string;
  description?: string;
  included?: string;
  notIncluded?: string;
};

type CarCategoryWithTranslations = CarCategory & {
  translations?: { [key in SupportedLocale]?: CarCategoryTranslation };
};

interface EstimateCarRentalHookProps {
  carCategory: CarCategoryWithTranslations | undefined;
  originalCurrency?: string;
  currency?: string;
  language?: SupportedLocale;
}

export const useEstimateCarRental = ({
  carCategory,
  originalCurrency,
  currency = 'EUR',
  language,
}: EstimateCarRentalHookProps) => {
  const { convertPrice } = useConvertPrice();
  const { t } = useTranslation();

  const convertIfOriginalCurrencyProvided = useCallback(
    (value: number | null | undefined) => {
      if (!value) return undefined;
      if (originalCurrency) {
        return roundUpPrice(convertPrice(value, originalCurrency, currency));
      }
      return value;
    },
    [convertPrice, currency, originalCurrency],
  );

  const carCategoryInformation: Record<string, string | undefined> =
    useMemo(() => {
      const concatOrUndefined = <T>(a: T | undefined | null, b: string) =>
        a ? `${a} ${b}` : undefined;
      const transformIfNotUndefined = <T>(
        a: T | undefined,
        compareValue: T,
        truthyValue?: string,
        falseValue?: string,
      ) =>
        a == undefined
          ? undefined
          : a === compareValue
            ? truthyValue
            : falseValue;

      const translatedCategory =
        language && carCategory?.translations?.[language];
      const included = translatedCategory?.included ?? carCategory?.included;
      const notIncluded =
        translatedCategory?.notIncluded ?? carCategory?.notIncluded;

      return {
        [t('estimates.details.carRentalElements.maxCapacity')]:
          concatOrUndefined(
            carCategory?.maxCapacity,
            t('estimates.details.carRentalElements.people'),
          ),
        [t('estimates.details.carRentalElements.maxNbLuggage')]:
          concatOrUndefined(carCategory?.maxNbLuggage, ''),
        [t('estimates.details.carRentalElements.gearBoxType')]:
          transformIfNotUndefined(
            carCategory?.gearBoxType,
            'manual',
            t('estimates.details.carRentalElements.manual'),
            t('estimates.details.carRentalElements.automatic'),
          ),
        [t('estimates.details.carRentalElements.airConditioning')]:
          transformIfNotUndefined(
            carCategory?.airConditioning,
            true,
            t('estimates.details.carRentalElements.yes'),
            t('estimates.details.carRentalElements.no'),
          ),
        [t('estimates.details.carRentalElements.includedMileage')]:
          transformIfNotUndefined(
            carCategory?.isIncludedMileageUnlimited,
            true,
            t('estimates.details.carRentalElements.unlimited'),
            concatOrUndefined(
              carCategory?.includedMileage,
              t('estimates.details.carRentalElements.kmPerDay'),
            ),
          ),
        [t('estimates.details.carRentalElements.additionalCost')]:
          transformIfNotUndefined(
            carCategory?.isIncludedMileageUnlimited,
            false,
            concatOrUndefined(
              convertIfOriginalCurrencyProvided(carCategory?.additionalCost),
              formatCurrency(currency),
            ),
            undefined,
          ),
        [t('estimates.details.carRentalElements.franchise')]: concatOrUndefined(
          convertIfOriginalCurrencyProvided(
            carCategory?.standardDeductableAmount,
          ),
          formatCurrency(currency),
        ),
        [t('estimates.details.carRentalElements.deposit')]: concatOrUndefined(
          convertIfOriginalCurrencyProvided(carCategory?.depositAmount),
          formatCurrency(currency),
        ),
        [t('estimates.details.carRentalElements.minimumAge')]:
          concatOrUndefined(
            carCategory?.minimumAge,
            t('estimates.details.carRentalElements.years'),
          ),
        [t('estimates.details.carRentalElements.included')]:
          included ?? undefined,
        [t('estimates.details.carRentalElements.notIncluded')]:
          notIncluded ?? undefined,
      };
    }, [
      carCategory?.additionalCost,
      carCategory?.airConditioning,
      carCategory?.depositAmount,
      carCategory?.gearBoxType,
      carCategory?.included,
      carCategory?.includedMileage,
      carCategory?.isIncludedMileageUnlimited,
      carCategory?.maxCapacity,
      carCategory?.maxNbLuggage,
      carCategory?.minimumAge,
      carCategory?.notIncluded,
      carCategory?.standardDeductableAmount,
      carCategory?.translations,
      convertIfOriginalCurrencyProvided,
      currency,
      language,
      t,
    ]);

  return { carCategoryInformation };
};
